import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { practiceDataUtil } from '../helpers/practiceDataUtil';
import { useToast } from '../hooks/use-toast';

// Create the context
const PracticeDataContext = createContext(null);

// Custom hook to use the practice data context
export function usePracticeData() {
  const context = useContext(PracticeDataContext);
  if (!context) {
    throw new Error('usePracticeData must be used within a PracticeDataProvider');
  }
  return context;
}

// Provider component
export function PracticeDataProvider({ children }) {
  // State for practice data
  const [allPractices, setAllPractices] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasUpdates, setHasUpdates] = useState(false);
  const [timestamp, setTimestamp] = useState(practiceDataUtil.getLastUpdateTimestamp());
  const [initialized, setInitialized] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track the content hash of practices to detect actual changes
  const practicesContentHashRef = useRef('');
  const lastProcessedTimestamp = useRef(null);

  const { toast } = useToast();

  // Initial loading of practices on component mount
  useEffect(() => {
    const loadPractices = async () => {
      if (initialized) return;      try {
        setLoading(true);
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 PracticeDataContext: Loading ALL practices from practiceDataUtil');
        }

        const practices = await practiceDataUtil.getAllPractices(true);        if (practices && practices.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`📥 PracticeDataContext: Loaded ${practices.length} practices initially`);
          }

          // Calculate a simple content hash by sampling some practices
          updatePracticeHash(practices);

          setAllPractices(practices);
          setLastRefreshTime(Date.now());
          setInitialized(true);        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ PracticeDataContext: Error loading practices:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPractices();
  }, [initialized]);
  // Helper to update practice content hash for change detection
  const updatePracticeHash = useCallback((practices) => {
    // Create a simple hash from a sample of practice names to detect content changes
    if (!practices || practices.length === 0) return;
    
    // Sample practices from throughout the array
    const sampleSize = Math.min(5, practices.length);
    const sampleIndices = [0, Math.floor(practices.length / 4), Math.floor(practices.length / 2),
      Math.floor(3 * practices.length / 4), practices.length - 1];

    const hash = sampleIndices
      .slice(0, sampleSize)
      .map(idx => {
        const practice = practices[idx];
        return `${practice.prac_code}:${practice.prac_name || 'unnamed'}`;
      })
      .join('|');    // Only log if the hash changed
    if (hash !== practicesContentHashRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`📊 Practice content hash changed: ${practicesContentHashRef.current} -> ${hash}`);
      }
      practicesContentHashRef.current = hash;
    }
  }, []);

  // Check for updates - this can be called manually by components
  const checkForUpdates = useCallback(async (forceCheck = false) => {
    if (isRefreshing && !forceCheck) return false;    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Checking for practice updates...');
      }
      const status = await practiceDataUtil.checkForUpdatesAvailable(true);

      const previousHasUpdates = hasUpdates;
      const updatesAvailable = status?.available || false;      setHasUpdates(updatesAvailable);

      if (previousHasUpdates !== updatesAvailable) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 Update status changed: ${previousHasUpdates} -> ${updatesAvailable}`);
        }
      }      return updatesAvailable;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ PracticeDataContext: Error checking for updates:', error);
      }
      return false;
    }
  }, [hasUpdates, isRefreshing]);

  // Run a single update check on initial load
  useEffect(() => {
    if (initialized) {
      checkForUpdates();
    }
  }, [initialized, checkForUpdates]);

  // Manual refresh function - called by UI components, not automatically
  const refreshPractices = useCallback(async (forceRefresh = true) => {    try {
      setLoading(true);
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 PracticeDataContext: Refreshing all practices, forceRefresh=${forceRefresh}`);
      }

      // Clear cache if the utility supports it
      if (practiceDataUtil.clearCache) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🧹 Clearing practiceDataUtil cache before refresh');
        }
        practiceDataUtil.clearCache();
      }

      const freshData = await practiceDataUtil.getAllPractices(true);      if (freshData && freshData.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 PracticeDataContext: Refreshed ${freshData.length} practices`);
        }

        // Update content hash to detect changes
        updatePracticeHash(freshData);

        setAllPractices(freshData);
        setHasUpdates(false);
        setLastRefreshTime(Date.now());        const newTimestamp = practiceDataUtil.getLastUpdateTimestamp();
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 Setting new timestamp: ${newTimestamp}`);
        }
        setTimestamp(newTimestamp);
        lastProcessedTimestamp.current = newTimestamp;

        toast({
          title: "Practices Refreshed",
          description: "Practice data has been updated with latest information",
          variant: "success"
        });

        return true;      }
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ PracticeDataContext: Error refreshing practices:', error);
      }

      toast({
        title: "Refresh Failed",
        description: "Could not refresh practice data",
        variant: "destructive"
      });

      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, updatePracticeHash]);

  // Get practice by code - utility function
  const getPractice = useCallback(async (code, forceRefresh = false) => {
    if (!code) return null;

    try {      return await practiceDataUtil.getPracticeByCode(code, forceRefresh);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('PracticeDataContext: Error getting practice:', error);
      }
      return null;
    }
  }, []);

  // Update selected practices
  const updateSelectedPractices = useCallback((practices) => {
    setSelectedPractices(practices);
  }, []);

  // Enhance practice codes with full practice data
  const enhancePractices = useCallback((practiceCodes) => {
    if (!practiceCodes?.length) return [];

    // allPractices not loaded yet — return minimal objects so the sync effect can
    // enhance them with real names once allPractices arrives (prevents the dropdown
    // staying empty due to the race between loadUserSelectedPractices and allPractices load)
    if (!allPractices.length) {
      return practiceCodes.map(code => {
        const practiceCode = typeof code === 'object' ? code.prac_code : code;
        const practiceName = typeof code === 'object' ? code.prac_name : code;
        return { prac_code: practiceCode, prac_name: practiceName, address4: '', postcode: '', address1: '' };
      });
    }

    const practiceMap = new Map(
      allPractices.map(practice => [practice.prac_code, practice])
    );

    return practiceCodes.map(code => {
      const practiceCode = typeof code === 'object' ? code.prac_code : code;
      const practice = practiceMap.get(practiceCode);

      if (practice) {
        return {
          prac_code: practiceCode,
          prac_name: practice.prac_name,
          address4: practice.address4 || '',
          postcode: practice.postcode || '',
          address1: practice.address1 || '',
        };
      }

      return {
        prac_code: practiceCode,
        prac_name: typeof code === 'object' ? code.prac_name : practiceCode,
        address4: '',
        postcode: '',
        address1: '',
      };
    });
  }, [allPractices]);

  // Load user's selected practices
  const loadUserSelectedPractices = useCallback(async (userId) => {
    if (!userId) return;    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('PracticeDataContext: Loading user selected practices');
      }

      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();        if (userData.selectedPractices && Array.isArray(userData.selectedPractices)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Found ${userData.selectedPractices.length} selected practices for user`);
          }

          const enhancedPractices = enhancePractices(userData.selectedPractices);
          setSelectedPractices(enhancedPractices);

          return enhancedPractices;
        }      }
      return [];
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('PracticeDataContext: Error loading selected practices:', error);
      }
      return [];
    }
  }, [enhancePractices]);

  // Process timestamp changes - but prevent duplicate processing
  useEffect(() => {
    const processTimestampChange = async () => {
      // Skip if conditions aren't right for processing
      if (!lastRefreshTime || isRefreshing || lastProcessedTimestamp.current === timestamp) {        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 Processing timestamp change: ${lastProcessedTimestamp.current || 'initial'} -> ${timestamp}`);
      }
      lastProcessedTimestamp.current = timestamp;

      try {
        setIsRefreshing(true);

        // Load fresh practice data
        const freshPractices = await practiceDataUtil.getAllPractices(true);

        if (freshPractices && freshPractices.length > 0) {
          // Check if content actually changed using our hash method
          const oldHash = practicesContentHashRef.current;
          updatePracticeHash(freshPractices);          if (oldHash !== practicesContentHashRef.current) {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 Practice content changed, updating all practices');
            }
            setAllPractices(freshPractices);
            setLastRefreshTime(Date.now());
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 Timestamp changed but content identical, skipping update');
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Error processing timestamp change:', error);
        }
      } finally {
        setIsRefreshing(false);
      }
    };

    processTimestampChange();
  }, [timestamp, updatePracticeHash]);

  // ENHANCED: Keep selected practices in sync with all practices with better change detection
  useEffect(() => {
    if (selectedPractices.length > 0 && allPractices.length > 0) {
      console.log('🔄 PracticeDataContext: Syncing selectedPractices with allPractices');      const selectedCodes = selectedPractices.map(p =>
        typeof p === 'object' ? p.prac_code : p
      );

      const freshPracticesMap = new Map();
      allPractices.forEach(practice => {
        if (selectedCodes.includes(practice.prac_code)) {
          freshPracticesMap.set(practice.prac_code, practice);
        }
      });

      const freshSelectedPractices = selectedCodes.map(code => {
        const freshPractice = freshPracticesMap.get(code);        if (freshPractice) {
          const existingPractice = selectedPractices.find(p =>
            (typeof p === 'object' ? p.prac_code : p) === code
          );

          return {
            prac_code: code,
            prac_name: freshPractice.prac_name,
            address4: freshPractice.address4 || '',
            postcode: freshPractice.postcode || '',
            address1: freshPractice.address1 || '',
          };
        }

        const existingPractice = selectedPractices.find(p =>
          (typeof p === 'object' ? p.prac_code : p) === code
        );
        return existingPractice || { prac_code: code, prac_name: code };
      });

      // ENHANCED: More robust comparison for detecting changes
      let needsUpdate = false;
      if (freshSelectedPractices.length !== selectedPractices.length) {
        needsUpdate = true;
      } else {
        for (let i = 0; i < freshSelectedPractices.length; i++) {
          const fresh = freshSelectedPractices[i];
          const current = selectedPractices[i];
            if (current && (
            current.prac_code !== fresh.prac_code || 
            current.prac_name !== fresh.prac_name
          )) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`🔄 Practice ${fresh.prac_code} changed: "${current.prac_name}" -> "${fresh.prac_name}"`);
            }
            needsUpdate = true;
            break;
          }
        }
      }      if (needsUpdate) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Updating selectedPractices with fresh data from allPractices');
          console.log('📊 New practice data:', freshSelectedPractices.map(p => `${p.prac_code}: "${p.prac_name}"`));
        }
        setSelectedPractices(freshSelectedPractices);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ No name changes detected, selectedPractices stay the same');
        }
      }
    }
  }, [allPractices, selectedPractices]);

  // Enhanced: Direct practice update mechanism that bypasses all caches
  const forceUpdatePracticeData = useCallback(async (practiceCode) => {
    if (!practiceCode) return false;
    
    console.log(`🔄 Forcing direct update for practice ${practiceCode}`);
    
    try {
      // Instead of using a non-existent method, check for updates and get fresh practice data
      const updateStatus = await practiceDataUtil.checkForUpdatesAvailable(true);
      
      if (!updateStatus || !updateStatus.available) {
        console.log('⚠️ No updates available according to practiceDataUtil');
        return false;
      }
      
      // Force refresh by getting fresh practice data directly
      const freshPractice = await practiceDataUtil.getPracticeByCode(practiceCode, true);
      
      if (!freshPractice) {
        console.log(`⚠️ Couldn't get fresh practice data for ${practiceCode}`);
        return false;
      }
      
      // Get the current version from allPractices
      const currentPractice = allPractices.find(p => p.prac_code === practiceCode);
      
      if (!currentPractice || currentPractice.prac_name === freshPractice.prac_name) {
        console.log('⚠️ No change detected in practice name');
        return false;
      }
      
      console.log(`🔄 Directly forcing practice name update: "${currentPractice.prac_name}" -> "${freshPractice.prac_name}"`);
      
      // Step 1: Update allPractices with the new data
      setAllPractices(currentPractices => 
        currentPractices.map(p => 
          p.prac_code === practiceCode 
            ? freshPractice // Use the entire fresh practice object
            : p
        )
      );
      
      // Step 2: Update selectedPractices if needed
      setSelectedPractices(currentSelected => 
        currentSelected.map(p => {
          if (typeof p === 'object' && p.prac_code === practiceCode) {
            return {
              ...p,
              prac_name: freshPractice.prac_name,
              address4: freshPractice.address4 || p.address4 || '',
              postcode: freshPractice.postcode || p.postcode || '',
              address1: freshPractice.address1 || p.address1 || ''
            };
          }
          return p;
        })
      );
      
      // Step 3: Force a timestamp change to trigger UI updates
      const newTimestamp = Date.now();
      setTimestamp(newTimestamp);
      
      // Set this directly so other checks see it as processed
      lastProcessedTimestamp.current = newTimestamp;
      
      console.log(`✅ Successfully forced direct update for ${practiceCode}`);
      return true;
    } catch (error) {
      console.error(`❌ Error directly updating practice ${practiceCode}:`, error);
      return false;
    }
  }, [allPractices]);

  // Export the context value
  const contextValue = {
    allPractices,
    selectedPractices,
    loading,
    hasUpdates,
    timestamp,
    refreshPractices,
    getPractice,
    updateSelectedPractices,
    enhancePractices,
    loadUserSelectedPractices,
    checkForUpdates,
    forceUpdatePracticeData  // Add this new function to context
  };

  return (
    <PracticeDataContext.Provider value={contextValue}>
      {children}
    </PracticeDataContext.Provider>
  );
}
